import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { DEFAULT_LANGUAGE } from '~constants/translations';
import { sentryError } from '~utils/sentry-error';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly http = inject(HttpClient);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly currentLang = signal<string>(DEFAULT_LANGUAGE);
  private readonly translationsSignal = signal<Record<string, string>>({});
  private loadingLang: string | null = null;
  private readonly supportedLanguages = [
    'en',
    'da',
    'de',
    'de_lu',
    'et',
    'fr_be',
    'fr_lu',
    'lt',
    'lv',
    'nl_be',
    'no',
    'pl',
    'sv',
    'fr',
    'nl',
  ];

  getLanguage(): string {
    return this.currentLang();
  }

  /**
   * Returns a language string stripped of its locale/region value.
   * @example de_LU -> de
   */
  getBaseLanguage(): string {
    return this.currentLang().split('_')[0];
  }

  /**
   * Returns a computed signal that translates a key.
   */
  translate(key: string): string {
    const translations = this.translationsSignal();

    return translations[key] || key;
  }

  setLanguage(lang: string): Observable<void> {
    const validatedLang = this.isLanguageValid(lang.toLowerCase()) ? lang.toLowerCase() : DEFAULT_LANGUAGE;

    // Prevent race condition: don't reload if already loading this language
    if (this.loadingLang === validatedLang) {
      return of(undefined); // Returning undefined completes the stream
    }

    // If language is already loaded, don't reload
    if (this.currentLang() === validatedLang && Object.keys(this.translationsSignal()).length > 0) {
      return of(undefined); // Returning undefined completes the stream
    }

    this.loadingLang = validatedLang;
    this.currentLang.set(validatedLang);

    return this.loadTranslations(validatedLang);
  }

  /**
   * Extracts the language parameter from the currently active route.
   *
   * Retrieves the 'lang' route parameter from the first child route of the activated route.
   *
   * @returns The language code from the route parameter, or 'en' as the default fallback
   */
  getLanguageFromRoute(): string {
    return this.activatedRoute.firstChild?.snapshot.paramMap.get('lang') || DEFAULT_LANGUAGE;
  }

  isLanguageValid(lang: string): boolean {
    return this.supportedLanguages.includes(lang);
  }

  private loadTranslations(lang: string): Observable<void> {
    return this.http.get<Record<string, string>>(`/assets/i18n/${lang}.json`).pipe(
      take(1),
      tap((translations) => {
        this.translationsSignal.set(translations);
        this.loadingLang = null;
      }),
      map(() => undefined),
      catchError((error) => {
        sentryError(error, 'Failed to load translations', `language: ${lang}`);
        this.translationsSignal.set({});
        this.loadingLang = null;

        return EMPTY;
      }),
    );
  }
}
